import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Mail, Lock, Eye, EyeOff, ArrowRight, Building2, Stethoscope, Shield, AlertCircle } from 'lucide-react'
import { useAuth } from '../../App'
import { AnimatedLogo } from '../../components/healthfluence'

function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [userType, setUserType] = useState('brand')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  const { login, getDashboardPath } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsLoading(true)
    setError('')

    try {
      const user = await login(email, password, userType)

      // Check if user needs to complete onboarding
      if (user && !user.onboardingComplete && user.userType !== 'admin') {
        navigate(`/onboarding/${user.userType}`)
      } else {
        // Navigate to appropriate dashboard
        if (userType === 'brand') {
          navigate('/brand')
        } else if (userType === 'creator') {
          navigate('/creator')
        } else {
          navigate('/admin')
        }
      }
    } catch (err) {
      setError('Invalid credentials. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const demoLogin = async (type) => {
    setUserType(type)
    setIsLoading(true)
    setError('')
    try {
      await login('demo@example.com', 'demo', type)
      navigate(`/${type}`)
    } catch (err) {
      setError('Demo login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm">
          <Link to="/" className="flex items-center space-x-2 mb-8">
            <AnimatedLogo size="xs" />
            <span className="text-xl font-bold bg-gradient-to-r from-hf-teal-600 to-hf-navy-700 bg-clip-text text-transparent">Healthfluence</span>
          </Link>

          <h2 className="text-3xl font-bold text-gray-900">Welcome back</h2>
          <p className="mt-2 text-gray-600">
            Sign in to your account to continue
          </p>

          {/* Demo Login Buttons */}
          <div className="mt-8">
            <p className="text-sm text-gray-500 mb-3">Quick demo access:</p>
            <div className="grid grid-cols-3 gap-3">
              <button
                onClick={() => demoLogin('brand')}
                disabled={isLoading}
                className="flex flex-col items-center justify-center p-3 bg-hf-teal-50 hover:bg-hf-teal-100 rounded-lg border-2 border-transparent hover:border-hf-teal-300 transition-all disabled:opacity-50"
              >
                <Building2 className="w-5 h-5 text-hf-teal-600 mb-1" />
                <span className="text-xs font-medium text-hf-teal-700">Brand</span>
              </button>
              <button
                onClick={() => demoLogin('creator')}
                disabled={isLoading}
                className="flex flex-col items-center justify-center p-3 bg-teal-50 hover:bg-teal-100 rounded-lg border-2 border-transparent hover:border-teal-300 transition-all disabled:opacity-50"
              >
                <Stethoscope className="w-5 h-5 text-teal-600 mb-1" />
                <span className="text-xs font-medium text-teal-700">Creator</span>
              </button>
              <button
                onClick={() => demoLogin('admin')}
                disabled={isLoading}
                className="flex flex-col items-center justify-center p-3 bg-purple-50 hover:bg-purple-100 rounded-lg border-2 border-transparent hover:border-purple-300 transition-all disabled:opacity-50"
              >
                <Shield className="w-5 h-5 text-purple-600 mb-1" />
                <span className="text-xs font-medium text-purple-700">Admin</span>
              </button>
            </div>
          </div>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-gray-50 text-gray-500">or sign in with email</span>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* User Type Selection */}
            <div>
              <label className="label">I am a...</label>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { id: 'brand', label: 'Brand', icon: Building2, color: 'hf-teal' },
                  { id: 'creator', label: 'Creator', icon: Stethoscope, color: 'teal' },
                  { id: 'admin', label: 'Admin', icon: Shield, color: 'purple' },
                ].map((type) => (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setUserType(type.id)}
                    className={`flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all ${
                      userType === type.id
                        ? type.id === 'brand'
                          ? 'border-hf-teal-500 bg-hf-teal-50'
                          : type.id === 'creator'
                            ? 'border-teal-500 bg-teal-50'
                            : 'border-purple-500 bg-purple-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <type.icon className={`w-5 h-5 mb-1 ${
                      userType === type.id
                        ? type.id === 'brand'
                          ? 'text-hf-teal-600'
                          : type.id === 'creator'
                            ? 'text-teal-600'
                            : 'text-purple-600'
                        : 'text-gray-400'
                    }`} />
                    <span className={`text-xs font-medium ${
                      userType === type.id
                        ? type.id === 'brand'
                          ? 'text-hf-teal-700'
                          : type.id === 'creator'
                            ? 'text-teal-700'
                            : 'text-purple-700'
                        : 'text-gray-600'
                    }`}>
                      {type.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="label">Email address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input pl-10"
                  placeholder="you@company.com"
                  required
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="label">Password</label>
                <Link to="/forgot-password" className="text-sm text-hf-teal-600 hover:text-hf-teal-700">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pl-10 pr-10"
                  placeholder="Your password"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3 rounded-lg font-medium flex items-center justify-center transition-colors ${
                userType === 'brand'
                  ? 'bg-hf-teal-600 hover:bg-hf-teal-700 text-white'
                  : userType === 'creator'
                    ? 'bg-teal-600 hover:bg-teal-700 text-white'
                    : 'bg-purple-600 hover:bg-purple-700 text-white'
              } disabled:opacity-50`}
            >
              {isLoading ? 'Signing in...' : 'Sign in'}
              {!isLoading && <ArrowRight className="ml-2 w-4 h-4" />}
            </button>
          </form>

          <p className="mt-8 text-center text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-hf-teal-600 hover:text-hf-teal-700 font-medium">
              Sign up
            </Link>
          </p>
        </div>
      </div>

      {/* Right Side - Branding */}
      <div className={`hidden lg:flex lg:flex-1 items-center justify-center p-12 ${
        userType === 'creator'
          ? 'bg-gradient-to-br from-teal-600 to-teal-700'
          : userType === 'admin'
            ? 'bg-gradient-to-br from-purple-600 to-purple-700'
            : 'bg-gradient-to-br from-hf-teal-600 to-hf-teal-700'
      }`}>
        <div className="max-w-md text-center">
          <div className="flex justify-center mb-8">
            <AnimatedLogo size="md" variant="light" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            Trust is the new attention
          </h2>
          <p className="text-white/80 text-lg">
            Ads rent eyeballs; influencers own trust. Join the platform that makes healthcare influence scalable, compliant, and measurable.
          </p>
          <div className="mt-12 grid grid-cols-3 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-white">10K+</p>
              <p className="text-white/70 text-sm">Creators</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">500+</p>
              <p className="text-white/70 text-sm">Brands</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-white">$50M+</p>
              <p className="text-white/70 text-sm">Processed</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default LoginPage
